/**
 * ============================================================================
 * УТИЛИТА ДЛЯ РАБОТЫ С URL ИЗОБРАЖЕНИЙ
 * ============================================================================
 * Формирует правильные URL для изображений с учетом окружения
 * ============================================================================
 */

/**
 * Получить базовый URL для изображений
 * В development используется proxy, в production - CDN или настроенный URL
 * @returns {string} Базовый URL
 */
function getBaseUrl() {
	// В production используем CDN если настроен
	if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_CDN_URL) {
		return process.env.VUE_APP_CDN_URL
	}

	// Или используем настроенный базовый URL для изображений
	if (process.env.VUE_APP_IMAGES_BASE_URL) {
		return process.env.VUE_APP_IMAGES_BASE_URL
	}

	// В development используем пустую строку (proxy обработает)
	return ''
}

/**
 * Получить полный URL изображения
 * @param {string} path - Относительный путь к изображению (например, /uploads/images/file.jpg)
 * @param {string} fallback - URL изображения по умолчанию
 * @returns {string} Полный URL изображения
 */
export function getImageUrl(path, fallback = null) {
	// Если путь пустой или null, возвращаем fallback
	if (!path) {
		return fallback || getDefaultCourseImage()
	}

	// Если это уже полный URL (начинается с http:// или https://), возвращаем как есть
	if (path.startsWith('http://') || path.startsWith('https://')) {
		return path
	}

	// Получаем базовый URL
	const baseUrl = getBaseUrl()

	// Если это относительный путь, добавляем базовый URL
	if (path.startsWith('/')) {
		return baseUrl + path
	}

	// Если путь не начинается с /, добавляем /uploads/
	return baseUrl + `/uploads/${path}`
}

/**
 * Получить URL изображения по умолчанию для курса
 * @returns {string} URL изображения по умолчанию
 */
export function getDefaultCourseImage() {
	return '/assets/animaapp/---------5@2x.png'
}

/**
 * Получить URL аватара пользователя
 * @param {string} path - Относительный путь к аватару
 * @returns {string} Полный URL аватара
 */
export function getAvatarUrl(path) {
	const defaultAvatar =
		'https://ui-avatars.com/api/?name=User&background=0095EC&color=fff'
	return getImageUrl(path, defaultAvatar)
}

/**
 * Получить URL обложки модуля
 * @param {string} path - Относительный путь к обложке
 * @returns {string} Полный URL обложки
 */
export function getModuleCoverUrl(path) {
	const defaultCover = '/assets/animaapp/---------5@2x.png'
	return getImageUrl(path, defaultCover)
}

/**
 * Проверить, является ли URL валидным изображением
 * @param {string} url - URL для проверки
 * @returns {Promise<boolean>} true если изображение доступно
 */
export async function isValidImageUrl(url) {
	return new Promise(resolve => {
		const img = new Image()
		img.onload = () => resolve(true)
		img.onerror = () => resolve(false)
		img.src = url
	})
}

/**
 * Получить оптимизированный URL изображения с параметрами
 * @param {string} path - Путь к изображению
 * @param {Object} options - Опции (width, height, quality)
 * @returns {string} URL с параметрами оптимизации
 */
export function getOptimizedImageUrl(path, _options = {}) {
	const url = getImageUrl(path)

	// В будущем можно добавить параметры для оптимизации
	// Например, если используется CDN с поддержкой resize
	// const { width, height, quality = 80 } = options
	// return `${url}?w=${width}&h=${height}&q=${quality}`

	return url
}

export default {
	getImageUrl,
	getDefaultCourseImage,
	getAvatarUrl,
	getModuleCoverUrl,
	isValidImageUrl,
	getOptimizedImageUrl,
}
